import Vue from "vue";

class ChatbotService {
  static get resource() {
    return "chatbot";
  }

  /**
   * Upload a file to the chatbot
   * @param {string} candidateId - The candidate ID
   * @param {File} file - The file to upload
   * @param {string} fileType - The type of file
   * @param {string} password - Optional password for AECB reports
   * @returns {Promise} Promise that resolves with upload response
   */
  static uploadFile(candidateId, file, fileType, password = null) {
    const url = `${this.resource}/uploads/${candidateId}`;
    const formData = new FormData();

    formData.append('file', file);
    formData.append('file_name', file.name);

    if (fileType === 'payer_list' || fileType === 'credit_model' || fileType === 'vendor_list') {
      formData.append('document_type', 'application/vnd.ms-excel');
    } else {
      formData.append('document_type', file.type);
    }

    if (password) {
      formData.append('password', password);
    }
    
    formData.append('type', fileType);

    return new Promise((resolve, reject) => {
      Vue.axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }


  /**
   * Get list of uploaded files
   * @param {string} candidateId - The candidate ID
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.page_size - Number of items per page
   * @param {string} params.type - Filter by file type (e.g., 'bank_statement', 'emirates_id', etc.)
   * @param {number} params.limit - Legacy limit parameter (deprecated, use page_size instead)
   * @returns {Promise} Promise that resolves with uploads list
   */
  static getUploads(candidateId, params = {}) {
    const url = `${this.resource}/uploads/${candidateId}`;
    
    // Build query parameters
    const queryParams = {};
    
    if (params.page !== undefined) {
      queryParams.page = params.page;
    }
    
    if (params.page_size !== undefined) {
      queryParams.page_size = params.page_size;
    }
    
    if (params.type && params.type !== 'all') {
      queryParams.type = params.type;
    }
    
    // Legacy support for limit parameter
    if (params.limit !== undefined && params.limit !== null) {
      queryParams.limit = params.limit;
    }

    return new Promise((resolve, reject) => {
      Vue.axios.get(url, { params: queryParams }).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }

  /**
   * Send a chat message/query
   * @param {Object} payload - The query payload
   * @param {string} payload.query - The user's query
   * @param {string} payload.file_name - The file name
   * @param {string} payload.document_type - The document type
   * @param {string} payload.vector_db_type - The vector database type (default: 'pinecone')
   * @param {boolean} payload.index - Whether to index (default: false)
   * @returns {Promise} Promise that resolves with chat response
   */
  static sendChatMessage(payload) {
    const url = `${this.resource}/file-query`;
    
    const requestPayload = {
      query: payload.query,
      file_name: payload.file_name,
      document_type: payload.document_type,
      vector_db_type: payload.vector_db_type || 'pinecone',
      index: payload.index || false
    };

    return new Promise((resolve, reject) => {
      Vue.axios.post(url, requestPayload).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }

  /**
   * Get task status for polling
   * @param {string} taskId - The task ID to check
   * @returns {Promise} Promise that resolves with task status
   */
  static getTaskStatus(taskId) {
    let url;
    
    // Check if this is a chat session query status
    if (taskId.includes('/query-status')) {
      // Extract candidate_id and session_id from the taskId
      const parts = taskId.split('/');
      if (parts.length >= 2) {
        const candidate_id = parts[0];
        const session_id = parts[1];
        url = `${this.resource}/chat-sessions/${candidate_id}/${session_id}/query-status`;
      } else {
        // Fallback to original format
        url = `${this.resource}/upload/${taskId}`;
      }
    } else {
      // Default query status endpoint
      url = `${this.resource}/upload/${taskId}`;
    }

    return new Promise((resolve, reject) => {
      Vue.axios.get(url).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }


  static generateReport(upload_id) {
    const url = `${this.resource}/report`;

    return new Promise((resolve, reject) => {
      Vue.axios.post(url, { upload_id: upload_id, format: 'excel' }, {
        responseType: 'blob'
      }).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }

  static generateCombinedReport(candidate_id) {
    const url = `${this.resource}/report`;

    return new Promise((resolve, reject) => {
      Vue.axios.post(url, { candidate_id: candidate_id, format: 'excel' }, {
        responseType: 'blob'
      }).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }

  static setFinancingCandidate(payload) {
    const url = `${this.resource}/financing-candidates`;

    payload.candidate_type = 'financing';

    return new Promise((resolve, reject) => {
      Vue.axios.post(url, payload).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }

  static getFinancingCandidates() {
    const url = `${this.resource}/financing-candidates`;

    return new Promise((resolve, reject) => {
      Vue.axios.get(url).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }

  static getFinancingCandidate(candidate_id) {
    const url = `${this.resource}/financing-candidates/${candidate_id}`;

    return new Promise((resolve, reject) => {
      Vue.axios.get(url).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }


  static getChatSessions(candidate_id) {
    const url = `${this.resource}/chat-sessions/${candidate_id}`;

    return new Promise((resolve, reject) => {
      Vue.axios.get(url).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }

  
  static createChatSession(candidate_id, payload) {
    const url = `${this.resource}/chat-sessions/${candidate_id}`;

    return new Promise((resolve, reject) => {
      Vue.axios.post(url, payload).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }


  static updateChatSession(candidate_id, session_id, payload) {
    const url = `${this.resource}/chat-sessions/${candidate_id}/${session_id}`;

    return new Promise((resolve, reject) => {
      Vue.axios.patch(url, payload).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }

  static deleteChatSession(candidate_id, session_id) {
    const url = `${this.resource}/chat-sessions/${candidate_id}/${session_id}`;

    return new Promise((resolve, reject) => {
      Vue.axios.delete(url).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }

  static getChatSessionHistory(candidate_id, session_id) {
    const url = `${this.resource}/chat-sessions/${candidate_id}/${session_id}`;

    return new Promise((resolve, reject) => {
      Vue.axios.get(url).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }

  /**
   * Send a chat message to a chat session
   * @param {string} candidate_id - The candidate ID
   * @param {string} session_id - The session ID
   * @param {Object} payload - The message payload
   * @param {string} payload.query - The user's query
   * @returns {Promise} Promise that resolves with the response
   */
  static sendChatMessageToSession(candidate_id, session_id, payload) {
    const url = `${this.resource}/chat-sessions/${candidate_id}/${session_id}`;

    payload.chat_type = 'financing';

    return new Promise((resolve, reject) => {
      Vue.axios.post(url, payload).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }

  static deleteFile(uploadId) {
    const url = `${this.resource}/upload/${uploadId}`;

    return new Promise((resolve, reject) => {
      Vue.axios.delete(url).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }

  /**
   * Upload multiple files in batch
   * @param {string} candidateId - The candidate ID
   * @param {File[]} files - Array of files to upload
   * @param {string} fileType - The type of file
   * @param {string} password - Optional password for AECB reports
   * @returns {Promise} Promise that resolves with batch upload response
   */
  static batchUploadFiles(candidateId, files, fileType, password = null) {
    const url = `${this.resource}/batch-uploads/${candidateId}`;
    const formData = new FormData();
    
    // Append each file to the form data
    files.forEach((file, index) => {
      formData.append(`files`, file);
    });

    if (password) {
      formData.append('password', password);
    }

    formData.append('type', fileType);

    return new Promise((resolve, reject) => {
      Vue.axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }

  /**
   * Get batch upload status
   * @param {string} batchId - The batch ID to check status for
   * @returns {Promise} Promise that resolves with batch status
   */
  static getBatchStatus(batchId) {
    const url = `${this.resource}/batch-status/${batchId}`;

    return new Promise((resolve, reject) => {
      Vue.axios.get(url).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }

  static getFileDetails(fileId) {
    const url = `${this.resource}/upload/${fileId}/details`;

    return new Promise((resolve, reject) => {
      Vue.axios.get(url).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }

  static getReportAnalysis(fileId, candidateId) {
    const url = `${this.resource}/financing-candidates/${candidateId}/credit-assessment-pdf`;
    return new Promise((resolve, reject) => {
      Vue.axios.get(url, { params: { file_id: fileId } }).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }

  static generateCreditModelReport(fileId, candidateId, payload = null) {
    const url = `${this.resource}/financing-candidates/${candidateId}/credit-assessment-pdf`;
    
    // Use provided payload or create default one
    const requestPayload = payload || { file_id: fileId };
    
    if (!requestPayload.file_id) {
      requestPayload.file_id = fileId;
    }
    
    return new Promise((resolve, reject) => {
      Vue.axios.post(url, requestPayload, {
        responseType: 'blob'
      }).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }

  static validateBankStatement(fileId) {
    const url = `${this.resource}/upload/${fileId}/validate`;
    return new Promise((resolve, reject) => {
      Vue.axios.post(url).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }

  static generateCompanyAnalysisPack(candidateId) {
    const url = `${this.resource}/financing-candidates/${candidateId}/analysis-pack`;
    return new Promise((resolve, reject) => {
      Vue.axios.post(url, null, {
        responseType: 'blob' // Important: tell axios to expect binary data
      }).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }

  static downloadFile(fileId) {
    const url = `${this.resource}/upload/${fileId}/download`;
    return new Promise((resolve, reject) => {
      Vue.axios.get(url, { responseType: 'blob' }).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }

  static matchFile(candidateId, payload) {
    const url = `${this.resource}/financing-candidates/${candidateId}/matching`;
    return new Promise((resolve, reject) => {
      Vue.axios.post(url, payload).then(({ data }) => {
        resolve(data);
      }).catch(error => {
        reject(error);
      });
    });
  }
}

export default ChatbotService; 